/**
 * « Ma carte » — tout le programme de l'année, sur deux axes (web-pwa §5.9).
 *
 * Demandé par Hedi le 6 septembre 2026 : *« je veux pour Mehdi avoir un écran
 * avec la carte complète des compétences comme pour les parents, avec
 * affichage de vue / non vue et travaillée / non travaillée »*.
 *
 * **Les deux axes ne disent pas la même chose, et c'est tout l'écran.** *Vue*
 * vient de l'école — cahier de textes, contenu de séance, journal du soir.
 * *Travaillée* vient d'ici — au moins une observation. « Non travaillée » est
 * normal quand la classe n'y est pas encore arrivée ; c'est un retard quand
 * elle y est passée. Les confondre, c'est ce que « Ma progression » fait
 * forcément : elle ne montre que ce qui a été mesuré.
 *
 * **Aucun chiffre sur l'élève** (invariant 1). Des effectifs — combien de
 * compétences dans telle case — jamais un pourcentage ni un rapport : une
 * proportion est la note sur vingt qui rentre par la porte de derrière.
 *
 * **Le rouge est réservé à ce qui appelle quelque chose.** « Pas encore vue »
 * s'affiche en neutre : le programme de mai n'est pas un manque en septembre,
 * et un écran qui le peindrait en alerte mettrait le calendrier au compte de
 * l'élève.
 *
 * **La fenêtre part de la rentrée, pas de sept jours.** C'est une carte de
 * l'année : une fenêtre courte ferait lire « non travaillée » sur tout ce qui a
 * été fait avant, et l'élève y verrait son propre retard là où il n'y a qu'une
 * borne. Le filtre est là — demandé par Hedi le 6 septembre 2026, *« rajoute la
 * possibilité de filtrer comme pour les parents »* — mais son défaut est la
 * réponse la plus large, pas la plus courte.
 *
 * **Les deux axes bougent ensemble.** Restreindre « travaillée » sans
 * restreindre « vue » ferait apparaître des rattrapages qui n'en sont pas ; le
 * serveur applique la fenêtre aux deux, et renvoie les bornes qu'il a
 * réellement appliquées — c'est celles-là que l'écran affiche.
 *
 * L'onglet n'est proposé qu'au profil lycée (`config/profile.ts`) : trois cents
 * lignes dont la plupart disent « pas encore » sont une carte pour quelqu'un qui
 * pilote son année, et un mur pour quelqu'un de douze ans. La route, elle,
 * répond pour les deux — ce sont les données de l'enfant, sur l'enfant.
 */

import { useEffect, useMemo, useState } from "react";

import { call } from "@/api/client";
import type { CarteCompetence, CarteDesCompetences, CarteMatiere } from "@/api/schema";
import { enClair } from "@/ui/dates";
import { bornes, jour, type Periode, type Preselection } from "@/ui/periode";
import { SelecteurDePeriode } from "@/ui/SelecteurDePeriode";
import { Button, Card, Empty, ErrorNote, Loading, Screen } from "@/ui/primitives";

/** Les quatre lectures de la carte. `rattraper` est la seule qui appelle un geste. */
export type Filtre = "tout" | "rattraper" | "vues" | "travaillees";

const FILTRES: { clef: Filtre; label: string }[] = [
  { clef: "tout", label: "Tout" },
  { clef: "rattraper", label: "À rattraper" },
  { clef: "vues", label: "Vues en cours" },
  { clef: "travaillees", label: "Travaillées ici" },
];

export function retenir(competencies: CarteCompetence[], filtre: Filtre): CarteCompetence[] {
  if (filtre === "vues") return competencies.filter((une) => une.vue);
  if (filtre === "travaillees") return competencies.filter((une) => une.travaillee);
  if (filtre === "rattraper") return competencies.filter((une) => une.vue && !une.travaillee);
  return competencies;
}

/** « 12 vues · 5 travaillées · 7 à rattraper », sur 115. Des effectifs, pas un ratio. */
export function decompte(matiere: CarteMatiere): string {
  return [
    `${matiere.vues ?? 0} vues en cours`,
    `${matiere.travaillees ?? 0} travaillées ici`,
    `${matiere.a_rattraper ?? 0} à rattraper`,
  ].join(" · ");
}

function Ligne({ competence }: { competence: CarteCompetence }) {
  return (
    <li className="carte__ligne">
      <span className="carte__label">{competence.label}</span>
      <span className="carte__marques">
        <span className="chip" data-on={competence.vue ? "oui" : "non"}>
          {competence.vue ? "vue en cours" : "pas encore vue"}
        </span>
        <span className="chip" data-on={competence.travaillee ? "oui" : "non"}>
          {competence.travaillee ? "travaillée" : "non travaillée"}
        </span>
      </span>
      {competence.reason ? (
        <span className="carte__raison muted small">{competence.reason}</span>
      ) : null}
    </li>
  );
}

function Matiere({ matiere, filtre }: { matiere: CarteMatiere; filtre: Filtre }) {
  const lignes = retenir(matiere.competencies ?? [], filtre);
  if (lignes.length === 0) return null;
  return (
    <Card title={matiere.label || matiere.subject}>
      <p className="muted small">
        {decompte(matiere)} — {matiere.total ?? 0} au programme cette année.
      </p>
      <ul className="carte__liste">
        {lignes.map((competence) => (
          <Ligne key={competence.competency_id} competence={competence} />
        ))}
      </ul>
    </Card>
  );
}

export function CarteScreen() {
  const [carte, setCarte] = useState<CarteDesCompetences | null>(null);
  const [failed, setFailed] = useState(false);
  const [filtre, setFiltre] = useState<Filtre>("tout");
  const aujourdhui = useMemo(() => new Date(), []);
  const [choix, setChoix] = useState<Preselection>("rentree");
  const [periode, setPeriode] = useState<Periode>(
    () => bornes("rentree", aujourdhui) ?? { since: "", until: jour(aujourdhui) },
  );

  useEffect(() => {
    if (!periode.since || !periode.until || periode.since > periode.until) return;
    let alive = true;
    setFailed(false);
    call("maCarte", { query: { since: periode.since, until: periode.until } })
      .then((data) => alive && setCarte(data))
      .catch(() => alive && setFailed(true));
    return () => {
      alive = false;
    };
  }, [periode.since, periode.until]);

  function choisir(valeur: Preselection) {
    setChoix(valeur);
    const calcul = bornes(valeur, aujourdhui);
    if (calcul) setPeriode(calcul);
  }

  function saisir(nouvelle: Periode) {
    setChoix("libre");
    setPeriode(nouvelle);
  }

  const matieres = useMemo(() => carte?.subjects ?? [], [carte]);

  if (failed) {
    return (
      <Screen title="Ma carte" lead="Tout le programme de l'année.">
        <ErrorNote>
          Je n'arrive pas à afficher ta carte pour l'instant. Réessaie tout à l'heure.
        </ErrorNote>
      </Screen>
    );
  }

  if (carte === null) {
    return (
      <Screen title="Ma carte" lead="Tout le programme de l'année.">
        <Loading />
      </Screen>
    );
  }

  const total = carte.total ?? 0;

  return (
    <Screen title="Ma carte" lead="Tout le programme de l'année, et où tu en es dessus.">
      <SelecteurDePeriode
        choix={choix}
        periode={periode}
        max={jour(aujourdhui)}
        onChoisir={choisir}
        onDates={saisir}
      />

      <Card title="Comment ça se lit">
        <p className="muted small">
          Deux questions différentes. <strong>Vue en cours</strong> : le cours est passé
          dessus, d'après le cahier de textes ou ce que tu as écrit le soir.{" "}
          <strong>Travaillée ici</strong> : tu as fait au moins un exercice dessus.
        </p>
        <p className="muted small">
          Une compétence pas encore vue n'est pas un retard — le programme n'y est pas
          arrivé. Ce qui se rattrape, c'est ce qui est vu en cours et jamais travaillé ici.
        </p>
      </Card>

      {total === 0 ? (
        <Card title="Tes matières">
          <Empty>Rien à montrer pour l'instant.</Empty>
        </Card>
      ) : (
        <>
          <Card title="En tout">
            <p className="muted small">
              {carte.vues ?? 0} vues en cours · {carte.travaillees ?? 0} travaillées ici ·{" "}
              {carte.a_rattraper ?? 0} à rattraper, sur {total} au programme.
            </p>
            {/* Les bornes que le serveur a **appliquées**, jamais celles
                demandées : il ramène une fin dans le futur à aujourd'hui, et
                l'écran doit dire ce qu'il montre. */}
            {carte.since && carte.until ? (
              <p className="muted small">
                Du {enClair(carte.since)} au {enClair(carte.until)}.
              </p>
            ) : null}
            {/* Une valeur non nulle veut dire qu'une matière manque au profil ou qu'un
                rattachement s'est trompé. Le dire plutôt que de laisser la carte
                paraître complète — c'est la même raison qui met un total à côté d'une
                liste vide côté parent. */}
            {(carte.hors_programme ?? 0) > 0 ? (
              <p className="muted small">
                {carte.hors_programme} notion{(carte.hors_programme ?? 0) > 1 ? "s" : ""} vue
                {(carte.hors_programme ?? 0) > 1 ? "s" : ""} en cours ne figure
                {(carte.hors_programme ?? 0) > 1 ? "nt" : ""} pas à ton programme ici.
              </p>
            ) : null}
            <div className="carte__filtres" role="group" aria-label="Filtrer la carte">
              {FILTRES.map((choix) => (
                <Button
                  key={choix.clef}
                  variant={filtre === choix.clef ? "secondary" : "quiet"}
                  aria-pressed={filtre === choix.clef}
                  onClick={() => setFiltre(choix.clef)}
                >
                  {choix.label}
                </Button>
              ))}
            </div>
          </Card>

          {matieres.every((matiere) => retenir(matiere.competencies ?? [], filtre).length === 0) ? (
            <Card title="Rien dans cette vue">
              <Empty>
                {filtre === "rattraper"
                  ? "Rien à rattraper : tout ce qui est passé en cours a été travaillé ici."
                  : "Rien à montrer avec ce filtre."}
              </Empty>
            </Card>
          ) : (
            matieres.map((matiere) => (
              <Matiere key={matiere.subject} matiere={matiere} filtre={filtre} />
            ))
          )}
        </>
      )}
    </Screen>
  );
}
